import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { IRootState } from 'src/reducers';
import PersonnelSearch from '../components/PersonnelSearch';
import BillingInfo from 'src/models/personnel/BillingInfo';
import EmployeePay from 'src/models/personnel/EmployeePay';
import useFetch from 'use-http';
import moment from 'moment';
import { TSDIPositions } from 'src/models/personnel/TSDIPosition';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import { UserRoles } from 'src/models/UserRole';
import DialogList from 'src/dialogs';
import EmployeePayHistoryDialog from '../dialogs/EmployeePayHistoryDialog';
import actions from 'src/actions';
import PersonnelNavBar from '../components/PersonnelNavBar';

const EmployeePayPage = () => {
    const dispatch = useDispatch();
    const { get, put, response } = useFetch();
    const { register, reset, handleSubmit, errors, getValues } = useForm();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [selectedEmployeePay, setSelectedEmployeePay] = useState(undefined);
    const [employeePayExceptions, setEmployeePayExceptions] = useState<BillingInfo[]>([]);
    const [employeePayHistory, setEmployeePayHistory] = useState(undefined);

    const appContext = useContext<IAppContextState>(AppContext);
    const isUserHR = appContext.currentUser.role === UserRoles.HR || appContext.currentUser.role === UserRoles.HRAdmin;
    let selectedPersonnel = useSelector((state: IRootState) => state.landman.selectedPersonnel);

    const positions  = [
        {id: 1, isSelected: false, name: 'AP/AR Clerk'},
        {id: 2, isSelected: false, name: 'Account Officer'},
        {id: 3, isSelected: false, name: 'Account Receivable Administrator'},
        {id: 4, isSelected: false, name: 'Account Associate'},
        {id: 5, isSelected: false, name: 'Account Manager'},
        {id: 6, isSelected: false, name: 'Account Receivable Coordinator'},
    ];

    // Initial render
    useEffect(() => {
        onCreate();

        // TODO: *cry* 😭✅
        return () => {
            onDestroy();
        };
    }, []);

    // SelectedPersonnel will change when the user clicks on a user or on initial render, if url contains an id
    useEffect(() => {
        if (!!selectedPersonnel) {
            getPersonnelEmployeePay();
            getEmployeePayHistory();
            getPersonnelBilling();
            console.log('====> SelectedPersonnel = ', selectedPersonnel);
        }
    }, [selectedPersonnel]);

    // SelectedEmployeePay changes on Update Pay
    useEffect(() => {
        console.log('resetting the form');
        reset(initializeData(selectedEmployeePay));
    }, [selectedEmployeePay]);

    useEffect(() => {
        console.log('Employee Pay Exceptions', employeePayExceptions);
    }, [employeePayExceptions]);

    /**
     * This function will be called when the Page is first rendered or the user enters the Page
     */
    const onCreate = () => {
        console.log('PersonnelPage onCreate()');
        if (!!selectedPersonnel) {
            getPersonnelEmployeePay();
            getEmployeePayHistory();
            getPersonnelBilling();
        }
    };

    /**
     * This function will be called when the user leaves the Page
     */
    const onDestroy = () => {
        console.log('PersonnelPage onDestroy()');
        dispatch({ type: '[landman] SET_SELECTED_PERSONNEL', payload: selectedPersonnel });
    };

    const initializeData = (data: EmployeePay) => {
        let newData: EmployeePay = { ...data };

        if (newData) {
            newData.changeDate = newData.changeDate ? (moment(newData.changeDate).format('YYYY-MM-DD') as unknown as Date) : null;
            newData.effectiveDate = newData.effectiveDate ? (moment(newData.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }

        return newData;
    };

    const onSubmit = async (data: EmployeePay) => {
        if (!!data) {
            let finalData = finalizeData(data);
            updateEmployeePay(finalData);
            updatePersonnelBilling(finalData.employeePayExceptions);
        }
    };

    const finalizeData = (data: EmployeePay) => {
        // Get all the form values, then add in the Personnel model values that the form doesn't have
        let finalData: EmployeePay = {
            ...data,
            userId: selectedPersonnel?.id,
            id: selectedPersonnel?.employeePay?.id
        };

        let values = getValues();
        console.log(values);

        finalData.payMethod = finalData.payMethod ? finalData.payMethod : null;
        finalData.changeDate = finalData.changeDate ? (moment(finalData.changeDate).format('YYYY-MM-DD') as unknown as Date) : null;
        finalData.effectiveDate = finalData.effectiveDate ? (moment(finalData.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null;

        // Fields hidden for non-HR Users
        isUserHR ? finalData.annualSalary = parseFloat(finalData.annualSalary + '') : finalData.annualSalary = selectedEmployeePay.annualSalary;
        isUserHR ? finalData.companyDayRate = parseFloat(finalData.companyDayRate + '') : finalData.companyDayRate = selectedEmployeePay.companyDayRate;
        isUserHR ? finalData.guaranteedRate = parseFloat(finalData.guaranteedRate + '') : finalData.guaranteedRate = selectedEmployeePay.guaranteedRate;
        isUserHR ? finalData.companyHourlyRate = parseFloat(finalData.companyHourlyRate + '') : finalData.companyHourlyRate = selectedEmployeePay.companyHourlyRate;

        finalData.hasProductionBonus = finalData.hasProductionBonus ? finalData.hasProductionBonus : false;
        finalData.vacationDays = finalData.vacationDays ? parseFloat(finalData.vacationDays + '') : null;
        finalData.personalDays = finalData.personalDays ? parseFloat(finalData.personalDays + '') : null;
        finalData.holidayDays = finalData.holidayDays ? parseFloat(finalData.holidayDays + '') : null;
        finalData.annualWorkersComp = finalData.annualWorkersComp ? parseFloat(finalData.annualWorkersComp + '') : null;

        finalData.positions.length = 0; 
        for (let i = 0; i < values?.positions?.length; i++) {
            finalData.positions.push({id: i, isSelected: values?.positions[i].isSelected, name: TSDIPositions[i]});
        }

        finalData.employeePayExceptions = null;

        finalData.isManager = finalData.isManager ? finalData.isManager : false;
        finalData.paidBy = finalData.paidBy ? finalData.paidBy : null;

        for (let i = 0; i < finalData?.employeePayExceptions?.length; i++) {
            finalData.employeePayExceptions[i].payRate = parseFloat(finalData.employeePayExceptions[i].payRate + '');
            finalData.employeePayExceptions[i].id = employeePayExceptions[i].id;
        }

        return finalData;
    };

    const getPersonnelBilling = async () => {
        let data = await get(`v1.0/landmen/${selectedPersonnel?.id}/get-personnel-billing`);

        if (response.ok) {
            setEmployeePayExceptions(data);
        } else {
            window.alert('Unable to Retrieve Selected Personnel\'s Billing Information.');
        }
    };

    const updatePersonnelBilling = async (data: BillingInfo[]) => {
        if (!!data) {
            await put(`v1.0/landmen/${selectedPersonnel?.id}/save-personnel-billing`, data);

            if (response.ok) {
                window.alert('Update Successful');
                getPersonnelBilling();
            } else {
                window.alert(`Unable to Update Selected Personnel\'s Billing Information.`);
            }
        }
    };

    const getPersonnelEmployeePay = async () => {
        if (selectedPersonnel) {
            let data = await get(`v1.0/landmen/${selectedPersonnel?.id}/get-personnel-employeePay`);

            if (response.ok) {
                setSelectedEmployeePay(data);
            } else {
                window.alert('Unable to Retrieve Selected Personnel\'s Billing Information.');
            }
        }
    };

    const getEmployeePayHistory = async () => {
        if (selectedPersonnel) {
            let data = await get(`v1.0/landmen/${selectedPersonnel?.id}/get-personnel-employeePay-history`);

            if (response.ok) {
                setEmployeePayHistory(data);
            } else {
                window.alert('Unable to Retrieve Selected Personnel\'s Billing Information.');
            }
        }
    };

    const updateEmployeePay = async (data: EmployeePay) => {
        if (selectedPersonnel) {
            let result = await put(`/v1.0/landmen/${selectedPersonnel?.id}/save-personnel-employeePay`, data);

            if (response.ok) {
                getPersonnelEmployeePay();
                getEmployeePayHistory();
                window.alert('Update successful');
            } else if (result?.messageCode === '15') {
                window.alert('Update failed');
            } else {
                window.alert('Update failed');
            }
        }
    };

    const showEmployeePayHistory = async () => {
        if (!!selectedPersonnel) {
            await getEmployeePayHistory();
            dispatch(actions.layout.showDialog(DialogList.EMPLOYEE_PAY_HISTORY));
        } else if (!selectedPersonnel) {
            window.alert('No Personnel Selected');
        }
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

    const showPersonnelData = () => {
        console.log('Display the Stuffs');
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Employee Pay - ${selectedPersonnel?.firstName} ${selectedPersonnel?.lastName}` : 'Employee Pay';

        return title;
    };

    const displayPositions = () => {
        return positions?.map((val) => {
            return (
                <div key={val.id - 1} className="form-row" style={{height: '1.5rem', marginTop: '1rem'}}>
                    <div />
                    <RHFCheckbox
                        name={`positions[${val.id - 1}].isSelected`}
                        register={register}
                        errors={errors}
                        bodyStyle={{width: '5%'}}
                    />
                    <div>{val.name}</div>
                </div>
            );
        });
    };

    const displayPayExceptions = () => {
        return <div>
            <table>
                <thead>
                    <tr>
                        <th>Client</th>
                        <th>Name</th>
                        <th>Project</th>
                        <th>Project Name</th>
                        <th>Pay Rate</th>
                        <th>Pay Unit</th>
                        <th>Classification</th>
                    </tr>
                </thead>
                {employeePayExceptions?.map((bill: BillingInfo, index: number) => {
                    return (
                        <tbody key={bill.id}>
                            <tr className="table-item-hover" style={{ textAlign: 'center' }}>
                                <td style={{ width: '5%' }}>
                                    {bill.clientId}
                                </td>
                                <td style={{ width: '20%' }}>
                                    {bill.clientName}
                                </td>
                                <td style={{ width: '5%' }}>
                                    {bill.projectId}
                                </td>
                                <td style={{ width: '20%' }}>
                                    {bill.projectName}
                                </td>
                                <td style={{ width: '5%' }}>
                                    { isUserHR && <RHFInputBox
                                        name={`employeePayExceptions[${index}].payRate`}
                                        type="number"
                                        defaultVal={`${bill.payRate}`}
                                        register={register}
                                        errors={errors}
                                        style={{ width: '100%' }}
                                        bodyStyle={{ width: '100%' }}
                                    />}
                                    { !isUserHR && <label style={{width: '100%'}}>NA</label>}
                                </td>
                                <td style={{ width: '10%' }}>
                                    { isUserHR && <RHFSelect
                                        name={`employeePayExceptions[${index}].payUnit`}
                                        defaultVal={bill.payUnit}
                                        options={[
                                            { value: 'Day', label: 'Day' },
                                            { value: 'Hour', label: 'Hour' },
                                        ]}
                                        register={register}
                                        errors={errors}
                                        style={{ width: '100%' }}
                                        bodyStyle={{ width: '100%' }}
                                    />}
                                    { !isUserHR && <label style={{width: '100%'}}>NA</label>}
                                </td>
                                <td style={{ width: '10%' }}>
                                    <RHFInputBox
                                        name={`employeePayExceptions[${index}].classificaton`}
                                        type="text"
                                        defaultVal={bill.classification}
                                        register={register}
                                        errors={errors}
                                        style={{ width: '100%' }}
                                        bodyStyle={{ width: '100%' }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    );
                })}
            </table>
        </div>;
    };

    const buttonClick = () => {
        console.log('Button Clicked');
    };

    return (
        <div style={{ margin: '1rem' }}>
            <PersonnelNavBar/>
            <PersonnelSearch showPersonnelData={showPersonnelData} setIsCollapsed={setIsCollapsed} />
            <form id="employeePayForm" onSubmit={handleSubmit(onSubmit)}>
                <Page>
                    <Page.Section width={8}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle={displayTitle()}>
                                {selectedPersonnel && <div style={ isUserHR ? {padding: '.5rem'} : { padding: '.5rem', paddingBottom: '2.5rem' }}>
                                    <div className="form-row" style={{ marginBottom: '0' }}>
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '24%', height: '3rem', marginLeft: '.5rem' }}
                                            text={'Update'}
                                            type="submit"
                                            form="employeePayForm"
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '24%', height: '3rem', marginLeft: '1rem' }}
                                            text={'History'}
                                            disabled={!isUserHR}
                                            onClick={() => showEmployeePayHistory()}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '24%', height: '3rem', marginLeft: '1rem' }}
                                            text={'Comments'}
                                            onClick={() => buttonClick}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '23%', height: '3rem', marginLeft: '1rem' }}
                                            text={'Vacation Bank'}
                                        />
                                    </div>
                                    { isUserHR && <div className="form-row" style={{ marginTop: '1rem' }}>
                                        <RHFInputBox
                                            label="Annual Salary"
                                            name={`annualSalary`}
                                            type="number"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '24%', marginLeft: '.5rem' }}
                                        />
                                        <RHFInputBox
                                            label="Guaranteed"
                                            name={`guaranteedRate`}
                                            type="number"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '24%' }}
                                        />
                                        <RHFInputBox
                                            label="Company Day Rate"
                                            name={`companyDayRate`}
                                            type="number"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '24%' }}
                                        />
                                        <RHFInputBox
                                            label="Company Hourly Rate"
                                            name={`companyHourlyRate`}
                                            type="number"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '23%' }}
                                        />
                                    </div>}
                                    { !isUserHR && <div className="form-row" style={{ marginTop: '1rem' }}>
                                        <RHFInputBox 
                                            name={`na`}
                                            placeholder="Unauthorized"
                                            type="number"
                                            style={{width: '100%'}}
                                            bodyStyle={{width: '24%', marginLeft: '.5rem'}}
                                            register={register} 
                                            errors={errors}
                                            disabled={true}
                                        />
                                        <RHFInputBox 
                                            name={`na`}
                                            placeholder="Unauthorized"
                                            type="number"
                                            style={{width: '100%'}}
                                            bodyStyle={{width: '24%'}}
                                            register={register} 
                                            errors={errors}
                                            disabled={true}
                                        />
                                        <RHFInputBox 
                                            name={`na`}
                                            placeholder="Unauthorized"
                                            type="number"
                                            style={{width: '100%'}}
                                            bodyStyle={{width: '24%'}}
                                            register={register} 
                                            errors={errors}
                                            disabled={true}
                                        />
                                        <RHFInputBox 
                                            name={`na`}
                                            placeholder="Unauthorized"
                                            type="number"
                                            style={{width: '100%'}}
                                            bodyStyle={{width: '23%'}}
                                            register={register} 
                                            errors={errors}
                                            disabled={true}
                                        />
                                    </div>}
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Ann. Work. Comp."
                                            name={`annualWorkersComp`}
                                            type="number"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '24%', marginLeft: '.5rem' }}
                                        />
                                        <RHFInputBox
                                            label="Change Date"
                                            name={`changeDate`}
                                            type="date"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '24%' }}
                                            disabled={true}
                                        />
                                        <RHFInputBox
                                            label="Eff. Date"
                                            name={`effectiveDate`}
                                            type="date"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '24%' }}
                                        />
                                        <RHFSelect
                                            label="Pay Method"
                                            name="payMethod"
                                            options={[
                                                { value: 'Hourly', label: 'Hourly - Part Time/Temporary/Clerical' },
                                                { value: 'Other', label: 'Other' },
                                            ]}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '23%' }}
                                        />
                                    </div>
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Vacation Days"
                                            name={`vacationDays`}
                                            type="number"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '24%', marginLeft: '.5rem' }}
                                        />
                                        <RHFInputBox
                                            label="Personal Days"
                                            name={`personalDays`}
                                            type="number"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '24%' }}
                                        />
                                        <RHFInputBox
                                            label="Holiday Days"
                                            name={`holidayDays`}
                                            type="number"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '24%' }}
                                        />
                                        <RHFCheckbox
                                            name="hasProductionBonus"
                                            label="Prod. Bonus?"
                                            register={register}
                                            bodyStyle={{ width: '23%', marginTop: '.5rem' }}
                                            errors={errors}
                                        />
                                    </div>
                                </div>}
                                {!selectedPersonnel && <div className="bold-centered">No Personnel Selected</div>}
                            </Panel>
                        </Panel>
                    </Page.Section>
                    <Page.Section width={4}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel panelTitle="TSDI Position">
                                {selectedPersonnel && <div style={{ padding: '.5rem' }}>
                                    <RHFCheckbox
                                        name="isManager"
                                        label="Manager?"
                                        labelStyle={{ marginLeft: '1.5rem' }}
                                        register={register}
                                        bodyStyle={{ width: '50%' }}
                                        errors={errors}
                                    />
                                    <hr className="divider" />
                                    <div style={{ overflowY: 'scroll', height: '13rem' }}>
                                        {displayPositions()}
                                    </div>
                                    <div className="form-row" style={{ textAlign: 'center', marginTop: '1rem' }}>
                                        <RHFSelect
                                            name={`paidBy`}
                                            placeholder="Paid By"
                                            options={[
                                                { value: 'Day', label: 'Day' },
                                                { value: 'Hour', label: 'Hour' },
                                                { value: '', label: 'Pay Unit' },
                                            ]}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '50%' }}
                                            register={register}
                                            errors={errors}
                                        />
                                    </div>
                                </div>}
                                {!selectedPersonnel && <div className="bold-centered">No Personnel Selected</div>}
                            </Panel>
                        </Panel>
                    </Page.Section>
                </Page>
                <Page>
                    <Page.Section width={12}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel>
                                {selectedPersonnel && <div className="form-row" style={{ marginBottom: '0' }}>
                                    <div
                                        className={isCollapsed ? 'small-panel' : 'small-panel-with-search'}
                                        style={{
                                            width: '78%',
                                            textAlign: 'center',
                                            overflowY: 'scroll'
                                        }}
                                    >
                                        {displayPayExceptions()}
                                    </div>
                                    <div style={{ top: '50%', width: '20%', marginLeft: '1rem', display: 'flex', flexDirection: 'column' }}>
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '100%', height: '3rem' }}
                                            text={'Benefit Breakdown'}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '100%', height: '3rem', marginTop: '.5rem' }}
                                            text={'Pay Exceptions'}
                                        />
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '100%', height: '3rem', marginTop: '.5rem' }}
                                            text={'Billing Exceptions'}
                                        />
                                    </div>
                                </div>}
                                {!selectedPersonnel && <div className="bold-centered">No Personnel Selected</div>}
                            </Panel>
                        </Panel>
                    </Page.Section>
                </Page>
                <EmployeePayHistoryDialog 
                    dialogName={DialogList.EMPLOYEE_PAY_HISTORY} 
                    hideDialog={hideDialog}
                    employeePayHistory={employeePayHistory}
                />
            </form>
        </div>
    );
};

export default EmployeePayPage;



// WEBPACK FOOTER //
// ./src/components/personnel/pages/EmployeePayPage.tsx